import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/config";
import { useAuth } from "@/lib/auth.jsx";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader, Pill, SectionCard } from "@/components/ui-bits";
import { fmtDate } from "@/lib/format";
import { Building2, KeyRound, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const USER_ROLES = ["Admin", "Business Development Partner", "Accountant", "Operations"];
const MODULES = [
  "Dashboard",
  "Leads",
  "Clients",
  "Projects",
  "Invoices",
  "Expenses",
  "Settings",
  "Reports",
];

const roleDefaults = (role) => {
  if (role === "Admin") return Object.fromEntries(MODULES.map((module) => [module, true]));
  if (role === "Business Development Partner") {
    return {
      Dashboard: true,
      Leads: true,
      Clients: false,
      Projects: false,
      Invoices: false,
      Expenses: false,
      Settings: false,
      Reports: false,
    };
  }
  if (role === "Accountant") {
    return {
      Dashboard: true,
      Leads: false,
      Clients: false,
      Projects: false,
      Invoices: true,
      Expenses: true,
      Settings: false,
      Reports: true,
    };
  }
  return {
    Dashboard: true,
    Leads: false,
    Clients: true,
    Projects: true,
    Invoices: false,
    Expenses: false,
    Settings: false,
    Reports: false,
  };
};

const emptyBrand = {
  name: "",
  code: "",
  invoice_prefix: "",
  proforma_prefix: "",
  email: "",
};

const emptyUser = {
  name: "",
  email: "",
  mobile: "",
  password: "",
  status: "Active",
  role: "Business Development Partner",
  assigned_brand_ids: [],
  module_permissions: roleDefaults("Business Development Partner"),
};

const visibleModulesLabel = (modules) =>
  MODULES.filter((module) => modules?.[module]).join(", ") || "No modules";

export default function Settings() {
  const { brands, reload } = useBrand();
  const { user, isAdmin, logout } = useAuth();
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [brandOpen, setBrandOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState(emptyBrand);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [userOpen, setUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState(emptyUser);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, userId: null, name: "", password: "" });
  const [selfPasswordForm, setSelfPasswordForm] = useState({ password: "", confirmPassword: "", saving: false });

  const loadSettings = useCallback(async () => {
    if (!isAdmin) return;
    const r = await api.get("/settings");
    setSettings(r.data || {});
  }, [isAdmin]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    const r = await api.get("/users");
    setUsers(r.data || []);
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadSettings();
    if (isAdmin) loadUsers();
  }, [isAdmin, loadSettings, loadUsers]);

  const set = (key, value) => setSettings((curr) => ({ ...curr, [key]: value }));

  const openCreateBrand = () => {
    setEditingBrand(null);
    setBrandForm(emptyBrand);
    setBrandOpen(true);
  };

  const openEditBrand = (brand) => {
    setEditingBrand(brand);
    setBrandForm({ ...emptyBrand, ...brand, email: brand.email || "" });
    setBrandOpen(true);
  };

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm(emptyUser);
    setUserOpen(true);
  };

  const openEditUser = (targetUser) => {
    setEditingUser(targetUser);
    setUserForm({
      name: targetUser.name || "",
      email: targetUser.email || "",
      mobile: targetUser.mobile || "",
      password: "",
      status: targetUser.status || "Active",
      role: targetUser.role || "Business Development Partner",
      assigned_brand_ids: targetUser.assigned_brand_ids || [],
      module_permissions: { ...roleDefaults(targetUser.role), ...(targetUser.module_permissions || {}) },
    });
    setUserOpen(true);
  };

  const saveCompany = async () => {
    try {
      await api.put("/settings", {
        company_name: settings.company_name,
        address: settings.address,
        phone: settings.phone,
        contact_email: settings.contact_email,
        gstin: settings.gstin,
        bank_name: settings.bank_name,
        account_name: settings.account_name,
        account_number: settings.account_number,
        ifsc: settings.ifsc,
        default_currency: settings.default_currency,
        default_gst_rate: Number(settings.default_gst_rate || 0),
      });
      toast.success("Company details saved");
      loadSettings();
    } catch {
      toast.error("Save failed");
    }
  };

  const saveEmail = async () => {
    try {
      await api.put("/settings", {
        resend_api_key: settings.resend_api_key === undefined ? undefined : settings.resend_api_key,
        sender_email: settings.sender_email,
      });
      toast.success("Email settings saved");
      setSettings((curr) => ({ ...curr, resend_api_key: "" }));
      loadSettings();
    } catch {
      toast.error("Save failed");
    }
  };

  const saveBrand = async () => {
    try {
      const payload = {
        name: brandForm.name,
        code: brandForm.code,
        invoice_prefix: brandForm.invoice_prefix,
        proforma_prefix: brandForm.proforma_prefix,
        email: brandForm.email || null,
      };
      if (editingBrand) await api.put(`/brands/${editingBrand.id}`, payload);
      else await api.post("/brands", payload);
      toast.success("Brand saved");
      setBrandOpen(false);
      reload();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save brand");
    }
  };

  const deleteBrand = async (brand) => {
    if (!window.confirm(`Delete brand ${brand.name}?`)) return;
    try {
      await api.delete(`/brands/${brand.id}`);
      reload();
      toast.success("Brand deleted");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete brand");
    }
  };

  const uploadLogo = async (brand, file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("PNG, JPG or WEBP only");
      return;
    }
    setUploadingFor(brand.id);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/brands/${brand.id}/logo`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      reload();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  const removeLogo = async (brand) => {
    if (!window.confirm("Remove this logo?")) return;
    await api.delete(`/brands/${brand.id}/logo`);
    reload();
  };

  const toggleBrand = (brandId, checked) => {
    setUserForm((curr) => ({
      ...curr,
      assigned_brand_ids: checked
        ? [...curr.assigned_brand_ids, brandId]
        : curr.assigned_brand_ids.filter((item) => item !== brandId),
    }));
  };

  const toggleModule = (module, checked) => {
    setUserForm((curr) => ({
      ...curr,
      module_permissions: { ...curr.module_permissions, [module]: checked },
    }));
  };

  const changeRole = (role) => {
    setUserForm((curr) => ({
      ...curr,
      role,
      module_permissions: roleDefaults(role),
    }));
  };

  const saveUser = async () => {
    try {
      const payload = {
        name: userForm.name,
        email: userForm.email,
        mobile: userForm.mobile,
        status: userForm.status,
        role: userForm.role,
        assigned_brand_ids: userForm.assigned_brand_ids,
        module_permissions: userForm.module_permissions,
      };
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...payload, password: userForm.password });
        toast.success("User created");
      }
      setUserOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save user");
    }
  };

  const savePassword = async () => {
    if (!passwordDialog.userId || !passwordDialog.password) return;
    try {
      await api.post(`/users/${passwordDialog.userId}/password`, { password: passwordDialog.password });
      toast.success("Password reset");
      setPasswordDialog({ open: false, userId: null, name: "", password: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not reset password");
    }
  };

  const saveOwnPassword = async () => {
    if (!selfPasswordForm.password || !selfPasswordForm.confirmPassword) {
      toast.error("Enter and confirm the new password");
      return;
    }
    if (selfPasswordForm.password !== selfPasswordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSelfPasswordForm((curr) => ({ ...curr, saving: true }));
    try {
      await api.post("/auth/change-password", { password: selfPasswordForm.password });
      setSelfPasswordForm({ password: "", confirmPassword: "", saving: false });
      toast.success("Password changed. Please sign in again.");
      logout();
    } catch (error) {
      setSelfPasswordForm((curr) => ({ ...curr, saving: false }));
      toast.error(error?.response?.data?.detail || "Could not change password");
    }
  };

  const quickStatusToggle = async (targetUser) => {
    const nextStatus = targetUser.status === "Active" ? "Disabled" : "Active";
    try {
      await api.put(`/users/${targetUser.id}`, { status: nextStatus });
      toast.success(`User ${nextStatus === "Active" ? "enabled" : "disabled"}`);
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update user status");
    }
  };

  const deleteUser = async (targetUser) => {
    if (!window.confirm(`Delete user ${targetUser.name}?`)) return;
    try {
      await api.delete(`/users/${targetUser.id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete user");
    }
  };

  const userStats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((item) => item.status === "Active").length,
      partners: users.filter((item) => item.role === "Business Development Partner").length,
    }),
    [users]
  );

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        description={
          isAdmin
            ? "Manage the parent company profile, brands, integrations, defaults, and enterprise user access."
            : "Change your password and re-enter the CRM with your updated credentials."
        }
      />

      <Tabs defaultValue={isAdmin ? "company" : "password"}>
        <TabsList className="border border-border bg-white">
          {isAdmin ? <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger> : null}
          {isAdmin ? <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger> : null}
          {isAdmin ? <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger> : null}
          {isAdmin ? <TabsTrigger value="defaults" data-testid="tab-defaults">Defaults</TabsTrigger> : null}
          {isAdmin ? <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger> : null}
          {!isAdmin ? <TabsTrigger value="password" data-testid="tab-password">Change Password</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="company" className="mt-5">
          <SectionCard title="Acharya Holdings — parent entity">
            <p className="text-xs text-muted-foreground mb-5 max-w-2xl">
              These details remain the parent source of truth across invoices, proformas, and brand-level documents.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
              <div className="md:col-span-2">
                <Label>Legal company name</Label>
                <Input value={settings.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea rows={2} value={settings.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={settings.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Contact email</Label>
                <Input value={settings.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input value={settings.gstin || ""} onChange={(e) => set("gstin", e.target.value)} className="mt-1 bg-white font-mono" />
              </div>
            </div>
            <div className="mt-7 mb-3 text-[10px] uppercase tracking-[0.18em] text-bronze font-medium">Bank for receivables</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
              <div>
                <Label>Bank name</Label>
                <Input value={settings.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Account name</Label>
                <Input value={settings.account_name || ""} onChange={(e) => set("account_name", e.target.value)} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Account number</Label>
                <Input value={settings.account_number || ""} onChange={(e) => set("account_number", e.target.value)} className="mt-1 bg-white font-mono" />
              </div>
              <div>
                <Label>IFSC</Label>
                <Input value={settings.ifsc || ""} onChange={(e) => set("ifsc", e.target.value)} className="mt-1 bg-white font-mono" />
              </div>
            </div>
            <div className="mt-5">
              <Button onClick={saveCompany} className="bg-bronze text-black hover:bg-[#d4af68]">Save company</Button>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="brands" className="mt-5">
          <SectionCard
            title="Brands under Acharya Holdings"
            action={
              <Button onClick={openCreateBrand} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="new-brand-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New brand
              </Button>
            }
          >
            <p className="text-xs text-muted-foreground mb-5 max-w-2xl">
              Parent GST, address, and banking remain shared. Brand-level differences are logo, invoice prefixes, and sender email.
            </p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {brands.map((brand) => (
                <div key={brand.id} className="rounded-xl border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg border border-border bg-[#fafaf9] flex items-center justify-center text-bronze font-mono text-xs">
                          {brand.code}
                        </div>
                        <div>
                          <div className="font-display font-medium text-slate-900">{brand.name}</div>
                          <div className="text-xs text-muted-foreground">{brand.email || "No sender email"}</div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1 text-sm text-slate-700">
                        <div>Invoice prefix: <span className="font-mono">{brand.invoice_prefix || "—"}</span></div>
                        <div>Proforma prefix: <span className="font-mono">{brand.proforma_prefix || "—"}</span></div>
                      </div>
                    </div>
                    <div className="w-28 shrink-0">
                      <div className="aspect-[16/9] rounded-lg border border-border bg-[#fafaf9] flex items-center justify-center overflow-hidden">
                        {brand.logo_url ? (
                          <img
                            src={`${BACKEND_URL}${brand.logo_url}?t=${Date.now()}`}
                            alt={`${brand.name} logo`}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> No logo
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex justify-center gap-2">
                        <label className="text-[11px] text-slate-600 hover:text-bronze cursor-pointer flex items-center gap-1">
                          <Upload className="w-3 h-3" />
                          {uploadingFor === brand.id ? "Uploading…" : brand.logo_url ? "Replace" : "Upload"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => uploadLogo(brand, e.target.files?.[0])}
                            disabled={uploadingFor === brand.id}
                          />
                        </label>
                        {brand.logo_url ? (
                          <button onClick={() => removeLogo(brand)} className="text-[11px] text-muted-foreground hover:text-rose-500 flex items-center gap-1">
                            <X className="w-3 h-3" /> Remove
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditBrand(brand)} className="border-border bg-white">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteBrand(brand)} className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50 ml-auto">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="email" className="mt-5">
          <SectionCard title="Resend integration">
            <p className="text-xs text-muted-foreground mb-5 max-w-2xl">
              Configure in-app invoice sending without exposing the raw API key back to the UI.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div className="md:col-span-2">
                <Label>Resend API key</Label>
                <Input
                  type="password"
                  value={settings.resend_api_key || ""}
                  onChange={(e) => set("resend_api_key", e.target.value)}
                  placeholder={settings.resend_api_key_masked || "re_..."}
                  className="mt-1 bg-white font-mono"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  {settings.resend_api_key_masked ? `Current: ${settings.resend_api_key_masked}` : "Not configured yet."}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Default sender email</Label>
                <Input value={settings.sender_email || ""} onChange={(e) => set("sender_email", e.target.value)} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="mt-5">
              <Button onClick={saveEmail} className="bg-bronze text-black hover:bg-[#d4af68]">Save email settings</Button>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="defaults" className="mt-5">
          <SectionCard title="Numerical defaults">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
              <div>
                <Label>Currency</Label>
                <Input value={settings.default_currency || "INR"} onChange={(e) => set("default_currency", e.target.value)} className="mt-1 bg-white font-mono" />
              </div>
              <div>
                <Label>Default GST %</Label>
                <Input type="number" value={settings.default_gst_rate ?? 18} onChange={(e) => set("default_gst_rate", e.target.value)} className="mt-1 bg-white" />
              </div>
            </div>
            <div className="mt-5">
              <Button onClick={saveCompany} className="bg-bronze text-black hover:bg-[#d4af68]">Save defaults</Button>
            </div>
          </SectionCard>
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="users" className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <SectionCard title="Total Users">
                <div className="text-3xl font-display font-semibold text-slate-900">{userStats.total}</div>
              </SectionCard>
              <SectionCard title="Active Users">
                <div className="text-3xl font-display font-semibold text-slate-900">{userStats.active}</div>
              </SectionCard>
              <SectionCard title="BD Partners">
                <div className="text-3xl font-display font-semibold text-slate-900">{userStats.partners}</div>
              </SectionCard>
            </div>

            <SectionCard
              title="User Management"
              action={
                <Button onClick={openCreateUser} className="bg-bronze text-black hover:bg-[#d4af68]">
                  <Plus className="w-4 h-4 mr-1.5" /> New user
                </Button>
              }
            >
              <div className="text-xs text-muted-foreground mb-4">
                Only Admin can create, edit, disable, delete, and reset user access. Assigned brands and module permissions are enforced by the backend.
              </div>
              <Table className="min-w-[980px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned Brands</TableHead>
                    <TableHead>Modules</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((item) => (
                    <TableRow key={item.id} className="bg-white">
                      <TableCell>
                        <div className="font-medium text-slate-900">{item.name}</div>
                        <div className="text-xs text-muted-foreground">{item.email}</div>
                        <div className="text-xs text-muted-foreground">{item.mobile || "No mobile"}</div>
                      </TableCell>
                      <TableCell>{item.role}</TableCell>
                      <TableCell>
                        <Pill tone={item.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                          {item.status}
                        </Pill>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(item.assigned_brand_ids || []).length
                            ? item.assigned_brand_ids.map((brandId) => {
                                const brand = brands.find((entry) => entry.id === brandId);
                                return <Pill key={brandId}>{brand?.code || brandId.slice(0, 4)}</Pill>;
                              })
                            : <span className="text-xs text-muted-foreground">All / none scoped</span>}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[280px] text-sm text-slate-700">{visibleModulesLabel(item.module_permissions)}</TableCell>
                      <TableCell>{item.last_login ? fmtDate(item.last_login) : "Never"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEditUser(item)} className="border-border bg-white">
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPasswordDialog({ open: true, userId: item.id, name: item.name, password: "" })}
                            className="border-border bg-white"
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1" /> Password
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => quickStatusToggle(item)}
                            className={item.status === "Active" ? "border-amber-200 bg-white text-amber-700 hover:bg-amber-50" : "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"}
                          >
                            {item.status === "Active" ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteUser(item)}
                            disabled={item.id === user?.id}
                            className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionCard>
          </TabsContent>
        ) : null}

        {!isAdmin ? (
          <TabsContent value="password" className="mt-5">
            <SectionCard title="Change Password">
              <div className="text-xs text-muted-foreground mb-5 max-w-xl">
                Update your password here. After saving, you will be logged out and must sign in again with the new password.
              </div>
              <div className="grid grid-cols-1 gap-4 max-w-xl">
                <div>
                  <Label>New password</Label>
                  <Input
                    type="password"
                    value={selfPasswordForm.password}
                    onChange={(e) => setSelfPasswordForm((curr) => ({ ...curr, password: e.target.value }))}
                    className="mt-1 bg-white"
                    data-testid="self-password"
                  />
                </div>
                <div>
                  <Label>Confirm new password</Label>
                  <Input
                    type="password"
                    value={selfPasswordForm.confirmPassword}
                    onChange={(e) => setSelfPasswordForm((curr) => ({ ...curr, confirmPassword: e.target.value }))}
                    className="mt-1 bg-white"
                    data-testid="self-password-confirm"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Button
                  onClick={saveOwnPassword}
                  disabled={selfPasswordForm.saving}
                  className="bg-bronze text-black hover:bg-[#d4af68]"
                  data-testid="self-password-save"
                >
                  {selfPasswordForm.saving ? "Saving..." : "Save password"}
                </Button>
              </div>
            </SectionCard>
          </TabsContent>
        ) : null}
      </Tabs>

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent className="max-w-lg" data-testid="brand-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editingBrand ? "Edit brand" : "New brand"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={brandForm.name} onChange={(e) => setBrandForm((curr) => ({ ...curr, name: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Code</Label>
              <Input value={brandForm.code} onChange={(e) => setBrandForm((curr) => ({ ...curr, code: e.target.value.toUpperCase() }))} className="mt-1 bg-white font-mono" maxLength={4} />
            </div>
            <div>
              <Label>Invoice prefix</Label>
              <Input value={brandForm.invoice_prefix} onChange={(e) => setBrandForm((curr) => ({ ...curr, invoice_prefix: e.target.value }))} className="mt-1 bg-white font-mono" />
            </div>
            <div>
              <Label>Proforma prefix</Label>
              <Input value={brandForm.proforma_prefix} onChange={(e) => setBrandForm((curr) => ({ ...curr, proforma_prefix: e.target.value }))} className="mt-1 bg-white font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label>Sender email</Label>
              <Input value={brandForm.email} onChange={(e) => setBrandForm((curr) => ({ ...curr, email: e.target.value }))} className="mt-1 bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrandOpen(false)}>Cancel</Button>
            <Button onClick={saveBrand} className="bg-bronze text-black hover:bg-[#d4af68]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editingUser ? "Edit user" : "Create user"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Name</Label>
              <Input value={userForm.name} onChange={(e) => setUserForm((curr) => ({ ...curr, name: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={userForm.email} onChange={(e) => setUserForm((curr) => ({ ...curr, email: e.target.value }))} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={userForm.mobile} onChange={(e) => setUserForm((curr) => ({ ...curr, mobile: e.target.value }))} className="mt-1 bg-white" />
            </div>
            {!editingUser ? (
              <div>
                <Label>Password</Label>
                <Input type="password" value={userForm.password} onChange={(e) => setUserForm((curr) => ({ ...curr, password: e.target.value }))} className="mt-1 bg-white" />
              </div>
            ) : (
              <div>
                <Label>Status</Label>
                <Select value={userForm.status} onValueChange={(value) => setUserForm((curr) => ({ ...curr, status: value }))}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editingUser ? (
              <div>
                <Label>Status</Label>
                <Select value={userForm.status} onValueChange={(value) => setUserForm((curr) => ({ ...curr, status: value }))}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={changeRole}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-2">
            <div className="rounded-xl border border-border bg-[#fafaf9] p-4">
              <div className="text-sm font-medium text-slate-900">Assigned Brands</div>
              <div className="text-xs text-muted-foreground mt-1">Users only see records for the brands checked here unless they are Admin.</div>
              <div className="mt-4 space-y-3">
                {brands.map((brand) => (
                  <label key={brand.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white p-3">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{brand.name}</div>
                      <div className="text-xs text-muted-foreground">{brand.code}</div>
                    </div>
                    <Checkbox
                      checked={userForm.assigned_brand_ids.includes(brand.id)}
                      onCheckedChange={(checked) => toggleBrand(brand.id, Boolean(checked))}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-[#fafaf9] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">Module Permissions</div>
                  <div className="text-xs text-muted-foreground mt-1">Backend access is enforced using these module flags.</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setUserForm((curr) => ({ ...curr, module_permissions: roleDefaults(curr.role) }))} className="border-border bg-white">
                  Use role defaults
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {MODULES.map((module) => (
                  <label key={module} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white p-3">
                    <div className="text-sm font-medium text-slate-900">{module}</div>
                    <Checkbox
                      checked={Boolean(userForm.module_permissions?.[module])}
                      onCheckedChange={(checked) => toggleModule(module, Boolean(checked))}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Cancel</Button>
            <Button onClick={saveUser} className="bg-bronze text-black hover:bg-[#d4af68]">
              {editingUser ? "Save user" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog((curr) => ({ ...curr, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Reset password for {passwordDialog.name}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>New password</Label>
            <Input
              type="password"
              value={passwordDialog.password}
              onChange={(e) => setPasswordDialog((curr) => ({ ...curr, password: e.target.value }))}
              className="mt-1 bg-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog({ open: false, userId: null, name: "", password: "" })}>Cancel</Button>
            <Button onClick={savePassword} className="bg-bronze text-black hover:bg-[#d4af68]">Save password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
